import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Feature, IgoMap, SpatialFilterOptions } from '@igo2/geo';
import { Geometry } from 'ol/geom';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { and, dwithin, intersects } from 'ol/format/filter';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, forkJoin, Observable, of, switchMap } from 'rxjs';
import { RapportXYDto } from 'src/app/shared/models/rapport-xy.dto';
import { printSection } from 'src/app/app.utils';
import { Constantes } from 'src/app/shared/constantes';
import { MapState, ToolState } from '@igo2/integration';
import { transform } from 'ol/proj';
import { MessageService } from '@igo2/core/message';
import { SpinnerComponent } from '@igo2/common/spinner';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import Filter from 'ol/format/filter/Filter';
import GeoJSON from 'ol/format/GeoJSON';
import WFS from 'ol/format/WFS';
import { Coordinate } from 'ol/coordinate';

interface serviceToCall {
  url: string;
  features: {
    layerName?: string;
    version: '1.1.0' | '2.0.0' | 'rest-form-data' | 'WMS';
    featurePrefix?: string;
    featureType?: string[];
    fields?: string[];
    filter?: Filter;
    dwithin?: number | number[];
  }[];
}

interface SigtSpatialFilterOptions {
  listServicesToCall: serviceToCall[]
}

@Component({
    selector: 'app-rapport-xy',
    templateUrl: './rapport-xy.component.html',
    styleUrls: ['./rapport-xy.component.scss'],
    imports: [
        CommonModule,
        MatButtonModule,
        TranslateModule,
        SpinnerComponent
    ]
})
export class RapportXyComponent implements OnInit, OnChanges {
  private listServicesToCall: serviceToCall[];
  private baseUrl: string;
  messageApplication: { [key: string]: string } = {};
  private wfsTools;
  rapportXyDto: RapportXYDto;
  postHeaders: HttpHeaders;
  viewportSize: any;
  coordinatesInPixels: any;
  bboxOfMap: string;
  pointIn3857: Coordinate;

  get map(): IgoMap {
    return this.mapState.map;
  }

  @Input()
  point: Feature;
  isLoading: boolean = false;

  constructor(
    private configService: ZzzConfigService,
    private httpClient: HttpClient,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private mapState: MapState,
    private toolState: ToolState
  ) { }

  ngOnInit(): void {
    this.messageApplication.echecConsulterDesServices = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    let options = this.toolState.toolbox.getTool("sigt-spatial-filter").options as SigtSpatialFilterOptions;
    this.listServicesToCall = options.listServicesToCall;
    this.baseUrl = this.configService.get().authApiEndPoint;
    this.wfsTools =
    {
      '2.0.0': new WFS({
        version: '2.0.0'
      }),
      '1.1.0': new WFS({
        version: '1.1.0'
      })
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    const point = changes['point']?.currentValue;
    if (!point) {
      this.rapportXyDto = undefined;
      return;
    }

    this.isLoading = true;
    this.initRapport(point);
    const requests$ = this.creerRequetes$(point);

    forkJoin(requests$).subscribe(reponses => {
      this.traiterReponses(reponses, point);
      this.isLoading = false;
      this.cdr.detectChanges();
    });
  }

  private initRapport(point: any) {
    this.rapportXyDto = new RapportXYDto(point.coordinates[0], point.coordinates[1]);
    this.postHeaders = new HttpHeaders({ interceptError: 'false' });
    //For WMS usage
    this.viewportSize = this.map.ol.getSize();
    this.pointIn3857 = transform(point.coordinates, 'EPSG:4326', 'EPSG:3857');
    this.coordinatesInPixels = this.map.ol.getPixelFromCoordinate(this.pointIn3857);
    // Emprise du canevas, calculée avec la même fonction que les pixels I/J du
    // point. viewController.getExtent() ne convient pas : avec une marge de vue
    // (panneau d'expansion ouvert : setPadding bottom 280), OpenLayers centre la
    // vue sur la zone visible, et l'emprise construite autour de ce centre est
    // décalée de la moitié de la marge. GetFeatureInfo interrogeait alors un
    // pixel décalé de ~140px : le lot voisin.
    const [largeur, hauteur] = this.viewportSize;
    const [minX, maxY] = this.map.ol.getCoordinateFromPixel([0, 0]);
    const [maxX, minY] = this.map.ol.getCoordinateFromPixel([largeur, hauteur]);
    this.bboxOfMap = [minX, minY, maxX, maxY].join(',');
  }

 private creerRequetes$(point: any): Observable<{ features: Feature[] }>[] {
    const geoJson = new GeoJSON();
    const geom = geoJson.readGeometry(point);

     let featureRequests = this.listServicesToCall.flatMap(svc =>
      svc.features.map(feat => {
        const serverUrl = this.baseUrl + svc.url;

        if (feat.version === 'WMS') {
          return this.creerRequeteWMS$(serverUrl, feat);
        }
        if (feat.version === 'rest-form-data') {
          return this.creerRequeteRestFormData$(serverUrl, feat, point);
        }
        // WFS
        return this.creerRequeteWFS$(serverUrl, feat, geom);
      })
    );
    return featureRequests;
  }

  private creerRequeteWMS$(
    serverUrl: string,
    feature: any
  ): Observable<{ features: Feature[] }> {
    let params = {
      SERVICE: 'WMS',
      VERSION: '1.3.0',
      REQUEST: 'GetFeatureInfo',
      INFO_FORMAT: 'application/json',
      CRS: this.map.projectionCode,
      LAYERS: feature.layerName,
      QUERY_LAYERS: feature.layerName,
      // Plusieurs éléments peuvent toucher le point dans la tolérance du serveur
      // (quelques pixels, soit quelques mètres à resolution 1). Par défaut, le
      // serveur n'en renvoie qu'un, pas forcément celui qui contient le point.
      FEATURE_COUNT: 10
    };

    if (feature.resolution) {
      let zoomParams = this.creerParamsWmsAutourPoint(this.pointIn3857[0], this.pointIn3857[1], feature.resolution, feature.width, feature.height)
      params = {... params, ... {
        BBOX: zoomParams.BBOX,
        WIDTH: zoomParams.WIDTH,
        HEIGHT: zoomParams.HEIGHT,
        I: zoomParams.I,
        J: zoomParams.J,
      }};
    }
    else
    {
      params = {... params, ... {
        BBOX: this.bboxOfMap,
        WIDTH: this.viewportSize[0],
        HEIGHT: this.viewportSize[1],
        I: Math.round(this.coordinatesInPixels[0]),
        J: Math.round(this.coordinatesInPixels[1]),
      }};
    }

    return this.httpClient
      .get<{ features: Feature[] }>(serverUrl, { params: params })
      .pipe(
        catchError(e => {
          this.messageService.error(this.messageApplication.echecConsulterDesServices);
          return of({ features: [] });
        })
      );
  }

  private creerParamsWmsAutourPoint(
    x: number, y: number, //en 3857
    resolution: number,   // Résolution cible en mètres/pixel
    width = 4000,          // Largeur de l'image en pixels
    height = 4000          // Hauteur de l'image en pixels
  ): {
    BBOX: string;
    WIDTH: number;
    HEIGHT: number;
    I: number;
    J: number;
  } {
    const bboxWidth = width * resolution;
    const bboxHeight = height * resolution;

    const minX = x - bboxWidth / 2;
    const minY = y - bboxHeight / 2;
    const maxX = x + bboxWidth / 2;
    const maxY = y + bboxHeight / 2;

    return {
      BBOX: `${minX},${minY},${maxX},${maxY}`,
      WIDTH: width,
      HEIGHT: height,
      I: Math.floor(width / 2),
      J: Math.floor(height / 2)
    };
  }

  private creerRequeteRestFormData$(
    serverUrl: string,
    feature: any,
    point: any
  ): Observable<{ features: Feature[] }> {
    const formData = new FormData();
    formData.append('f', 'json');
    formData.append('geometryType', 'esriGeometryPoint');
    formData.append('inSR', '4326');
    formData.append('spatialRel', 'esriSpatialRelIntersects');
    formData.append('geometry', `${point.coordinates[0]},${point.coordinates[1]}`);
    formData.append('outFields', feature.fields.join(','));

    return this.httpClient
      .post<{ features: Feature[] }>(serverUrl, formData, { headers: this.postHeaders })
      .pipe(
        catchError(e => {
          this.messageService.error(this.messageApplication.echecConsulterDesServices);
          return of({ features: [] });
        })
      );
  }

  private creerRequeteWFS$(
    serverUrl: string,
    feature: any,
    geom: Geometry
  ): Observable<{ features: Feature[] }> {

    const appelService = (rayon?: number) => {
      const payload = this.creerPayloadWFS(feature, serverUrl, geom, rayon);
      return this.httpClient
        .post<{ features: Feature[] }>(serverUrl, payload, { headers: this.postHeaders })
        .pipe(
          catchError(e => {
            this.messageService.error(this.messageApplication.echecConsulterDesServices);
            return of({ features: [] });
          })
        );
      };

    if (Array.isArray(feature.dwithin)) {
      // Retry automatique avec plusieurs distances (ex: GRHQToponyme)
      // Avant avec un seul appel sur 50km, il y a des endroits qui contiennent plus de lacs que le nombre maximum N de features retournées par geoserver.
      // Ainsi parfois le lac le plus proche pouvait ne pas être dans ces N lacs et donc notre rapport retournait faussement un lac plus loin.
      const result$ = (feature.dwithin as number[]).slice(1).reduce(
        (obs$: Observable<{ features: Feature[] }>, nextD) =>
          obs$.pipe(
            switchMap(resp =>
              resp.features.length > 0
                ? of(resp)
                : appelService(nextD)
            )
          ),
        appelService(feature.dwithin[0])
      );
      return result$;
    }

    return appelService(feature.dwithin);
  }

  private creerPayloadWFS(feature: any,
    serverUrl: string,
    geom: Geometry,
    rayon?: number
  ): string {
    const geomFilter = rayon
      ? dwithin('GEOMETRIE', geom, rayon, 'meters')
      : intersects('GEOMETRIE', geom);
    const wfsFilter = feature.filter
      ? and(feature.filter, geomFilter)
      : geomFilter;
    const req = this.wfsTools[feature.version].writeGetFeature({
      srsName:       'EPSG:4326',
      featureNS:     serverUrl,
      featurePrefix: feature.featurePrefix,
      featureTypes:  feature.featureType,
      outputFormat:  'application/json',
      filter:        wfsFilter
    });
    return new XMLSerializer().serializeToString(req);
  }

  private traiterReponses(responses: { features: Feature[] }[], point: any) {
    let index = 0;
    const geoJson = new GeoJSON();
    const geom    = geoJson.readGeometry(point);

    this.listServicesToCall.forEach(serviceToCall => {
      serviceToCall.features.forEach(feature => {
        let resp = responses[index++] || { features: [] };
        //service returned an error object with nofeatures
        if (!resp.hasOwnProperty("features")){
          resp = { features: [] };
        }

        // WFS
        feature.featureType?.forEach(ft => {
          const matches = resp.features.filter(f => (f as any).id.startsWith(ft));
          if (matches.length) {
            this.rapportXyDto.buildDataFor(ft, matches, geom);
          }
        });

        // rest-form-data
        if (feature.version === 'rest-form-data') {
          this.rapportXyDto.buildDataForRestRequest(feature.layerName, resp.features[0]);
        }

        // WMS
        if (feature.version === 'WMS') {
          this.rapportXyDto.buildDataForWmsRequest(feature.layerName, this.trierParContenance(resp.features));
        }
      });
    });
  }

  /**
   * Ne garde des surfaces que celles qui contiennent le point cliqué, en tête.
   * GetFeatureInfo renvoie tout ce qui touche le point dans sa tolérance, de
   * quelques pixels : à 1 m/pixel, un lot à quelques mètres du point est
   * renvoyé alors que le point n'est dans aucun lot, et près d'une limite, le
   * lot voisin arrive parfois en premier.
   * Les éléments sans géométrie, ou qui ne sont pas des surfaces (points,
   * lignes, qu'un point ne peut pas « contenir »), sont gardés après, dans
   * l'ordre du serveur. Les géométries de la réponse sont dans la projection
   * de la requête (3857), celle de pointIn3857.
   */
  private trierParContenance(features: Feature[]): Feature[] {
    const geoJson = new GeoJSON();
    const contient: Feature[] = [];
    const autres: Feature[] = [];
    features.forEach((f: any) => {
      let geometrie;
      try {
        geometrie = f.geometry ? geoJson.readGeometry(f.geometry) : undefined;
      } catch {
        geometrie = undefined;
      }
      const estSurface = geometrie?.getType() === 'Polygon' || geometrie?.getType() === 'MultiPolygon';
      if (!estSurface) {
        autres.push(f);
      } else if (geometrie.intersectsCoordinate(this.pointIn3857)) {
        contient.push(f);
      }
    });
    return [...contient, ...autres];
  }

  print() {
    printSection("divXYReport");
  }
}
